/**
 * ValuesTab — Platform Console · the crew's CHOSEN standards.
 *
 * Sub-tabs:
 *   - Overview  — taxonomy health: six value statements with committed
 *                 child counts + pending-classification banner.
 *   - Registry  — the full values registry (By Value / By Category views).
 *   - Tech Debt — the tech-debt summarization, rendered as-is (moved here
 *                 from its old top-level tab; drift-from-doc is a
 *                 direction-document-honesty concern).
 *
 * Evaluation stays in Release Ops (Values Engine card). This tab is the
 * detail surface. The steward's decision queue lives in Stewardship.
 */
import { useEffect, useMemo, useState } from "react";
import { Layers, RefreshCw, LayoutDashboard, ListTree, Wrench, AlertTriangle, GitBranch, Scale } from "lucide-react";
import { api } from "@/lib/api";
import { SubTabs, CountsStrip, ByValueView, ByCategoryView } from "@/components/site-admin/values-registry/RegistryShared";
import { LadderView } from "@/components/site-admin/values-registry/LadderView";
import PlatformTechDebtTab from "@/components/site-admin/PlatformTechDebtTab";

const SUB_TABS = [
    { k: "overview", label: "Overview", icon: LayoutDashboard },
    { k: "standards", label: "Standards", icon: Scale },
    { k: "ladder", label: "Ladder", icon: GitBranch },
    { k: "registry", label: "Registry", icon: ListTree },
    { k: "tech_debt", label: "Tech Debt", icon: Wrench },
];

function StandardsCatalogView() {
    // iter86.29.1 — the Standards Catalog (values_registry.md §4) surfaced
    // in-console; previously only readable via Reference Docs.
    const [catalog, setCatalog] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        api.tclMapping()
            .then((r) => setCatalog(r.standards_catalog || []))
            .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load"));
    }, []);
    if (error) return <div className="text-xs text-rose-300" data-testid="standards-catalog-error">{error}</div>;
    if (!catalog) return <div className="text-xs text-zinc-500" data-testid="standards-catalog-loading">Loading catalog…</div>;
    const groups = [
        { k: "crew-level", label: "Crew-level — ladder up to TCL" },
        { k: "game-specific", label: "Game-specific — do not ladder up" },
    ];
    return (
        <div className="space-y-5" data-testid="standards-catalog-view">
            <p className="text-[11px] text-zinc-500">
                {catalog.length} standards — the middle rung of the ladder (Values → Standards → Signals).
                Source of truth: <code>values_registry.md</code> §4. Mapping decisions live in Stewardship → TCL Mapping.
            </p>
            {groups.map((g) => {
                const rows = catalog.filter((s) => s.scope === g.k);
                if (!rows.length) return null;
                return (
                    <div key={g.k}>
                        <h3 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
                            {g.label} · {rows.length}
                        </h3>
                        <div className="space-y-2">
                            {rows.map((s) => (
                                <div
                                    key={s.standard_id}
                                    className="border border-zinc-800 rounded-lg p-3 bg-zinc-950/60"
                                    data-testid={`standards-catalog-row-${s.standard_id.replace(/[^a-z0-9-]/gi, "_")}`}
                                >
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <code className="text-xs font-bold text-fuchsia-200">{s.standard_id}</code>
                                        <span className={`pill text-[9px] font-bold uppercase border ${s.status === "active" ? "bg-emerald-900/40 text-emerald-200 border-emerald-700" : "bg-zinc-800 text-zinc-400 border-zinc-600"}`}>
                                            {s.status}
                                        </span>
                                        {s.value_group && (
                                            <span className="text-[10px] text-cyan-300">{s.value_group}</span>
                                        )}
                                        {s.expresses_tcl?.length > 0 && (
                                            <span className="ml-auto text-[10px] text-zinc-500">↑ {s.expresses_tcl.join(", ")}</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-zinc-300 leading-relaxed mb-1.5">{s.statement}</p>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {(s.signals || []).map((sig) => (
                                            <span key={sig} className="pill text-[9px] border bg-zinc-900 text-zinc-400 border-zinc-700">{sig}</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function OverviewView({ data }) {
    const slugs = Object.keys(data.value_groups || {}).filter(
        (s) => data.value_groups[s]?.statement
    );
    const pending = data.pending_count || 0;
    return (
        <div className="space-y-4" data-testid="values-overview-view">
            {pending > 0 && (
                <div
                    className="flex items-start gap-2 border border-amber-500/40 bg-amber-500/5 rounded p-3 text-xs text-amber-200"
                    data-testid="values-overview-pending-banner"
                >
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-400" />
                    <div>
                        <span className="font-bold">{pending} entries pending value classification.</span>{" "}
                        <span className="text-zinc-400">
                            Review the proposals in Stewardship → Actions or on the Release Ops Values Engine card. Visible warning only — never blocks a release.
                        </span>
                    </div>
                </div>
            )}

            <CountsStrip counts={data.counts} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {slugs.map((slug) => {
                    const node = data.value_groups[slug];
                    return (
                        <div
                            key={slug}
                            className="border-2 border-fuchsia-900/40 rounded-lg p-4 bg-fuchsia-950/10"
                            data-testid={`values-overview-card-${slug.replace(/[^a-z0-9-]/gi, "_")}`}
                        >
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                <h3 className="font-display font-extrabold text-xs uppercase tracking-wide text-fuchsia-200">
                                    {slug}
                                </h3>
                                <span className="ml-auto text-[10px] text-zinc-500">
                                    {node.active_children} committed
                                </span>
                            </div>
                            <p className="text-xs text-zinc-300 italic leading-relaxed">“{node.statement}”</p>
                        </div>
                    );
                })}
            </div>

            <p className="text-[10px] text-zinc-600">
                Values are <span className="text-zinc-400 font-bold">chosen</span> — what the crew believes.
                Externally-imposed requirements live in the Obligations tab.
                Source: <code>{data.source}</code>
            </p>
        </div>
    );
}

function RegistryView({ data }) {
    const [mode, setMode] = useState("by_value");
    return (
        <div className="space-y-3" data-testid="values-registry-view">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <CountsStrip counts={data.counts} />
                <SubTabs
                    items={[
                        { k: "by_value", label: "By Value", icon: Layers },
                        { k: "by_category", label: "By Category", icon: ListTree },
                    ]}
                    active={mode}
                    onChange={setMode}
                    testPrefix="guidance-view"
                />
            </div>
            {mode === "by_value" ? <ByValueView data={data} /> : <ByCategoryView data={data} />}
        </div>
    );
}

export default function ValuesTab() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sub, setSub] = useState("overview");

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await api.releaseOpsValuesRegistry());
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const generatedAt = useMemo(() => {
        if (!data?.generated_at) return null;
        return data.generated_at.replace("T", " ").replace(/\.\d+Z?$/, "Z");
    }, [data]);

    return (
        <div className="space-y-4" data-testid="values-tab">
            <div className="brutal-card p-4 border-2 border-fuchsia-900/40 bg-fuchsia-950/10">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Layers className="w-4 h-4 text-fuchsia-300" />
                            <h2 className="font-display font-extrabold text-lg uppercase tracking-wide text-white">
                                Values — Chosen Standards
                            </h2>
                            <span className="pill text-[9px] font-bold uppercase border bg-fuchsia-900/40 text-fuchsia-200 border-fuchsia-700">
                                Canon Placeholder
                            </span>
                        </div>
                        <div className="text-xs text-zinc-400">
                            The six values the crew holds, and every check and gate that expresses them.
                            Evaluation happens in Release Ops; this is the detail surface.
                            {generatedAt && (
                                <>
                                    {" "}<span className="text-zinc-600">·</span>{" "}
                                    <span className="text-zinc-500">Updated {generatedAt}</span>
                                </>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide bg-zinc-900 border border-zinc-700 rounded hover:text-white"
                        data-testid="values-refresh-btn"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>
            </div>

            <SubTabs items={SUB_TABS} active={sub} onChange={setSub} testPrefix="values-subtab" />

            {error && (
                <div className="text-xs text-rose-300 bg-rose-950/40 border border-rose-800 rounded p-3" data-testid="values-error">
                    {error}
                </div>
            )}
            {!data && !error && sub !== "tech_debt" && (
                <div className="text-xs text-zinc-500" data-testid="values-loading">Loading registry…</div>
            )}

            {sub === "overview" && data && <div className="brutal-card p-4"><OverviewView data={data} /></div>}
            {sub === "standards" && <div className="brutal-card p-4"><StandardsCatalogView /></div>}
            {sub === "ladder" && data && <div className="brutal-card p-4"><LadderView data={data} /></div>}
            {sub === "registry" && data && <div className="brutal-card p-4"><RegistryView data={data} /></div>}
            {sub === "tech_debt" && <PlatformTechDebtTab />}
        </div>
    );
}
